import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { MemberNameLink } from '@/components/shared/member-name-link';
import { PageHeader } from '@/components/shared/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { FollowUpDialog, type FollowUpRow } from '@/pages/people/follow-up-dialog';
import { PeopleSubnav } from '@/pages/people/people-subnav';
import { formatDate } from '@/utils/format';

export const FollowUpsPage = () => {
  const { t } = useTranslation('people');
  const [filter, setFilter] = useState<'open' | 'unassigned' | 'overdue'>('open');
  const [selected, setSelected] = useState<FollowUpRow | null>(null);

  const list = useQuery({
    queryKey: ['people', 'follow-ups', filter],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filter === 'open') params.set('status', 'open');
      if (filter === 'unassigned') params.set('unassigned', 'true');
      if (filter === 'overdue') params.set('overdue', 'true');
      return api.get<{ data: FollowUpRow[] }>(`/people/follow-ups?${params.toString()}`);
    },
  });

  const columns: DataTableColumn<FollowUpRow>[] = [
    {
      id: 'dueDate',
      header: t('followUps.columns.dueDate'),
      cell: (row) => (row.dueDate ? formatDate(row.dueDate) : '—'),
    },
    {
      id: 'person',
      header: t('followUps.columns.person'),
      cell: (row) => <MemberNameLink memberId={row.memberId} name={row.memberName} />,
    },
    {
      id: 'reason',
      header: t('followUps.columns.reason'),
      cell: (row) => t(`followUps.reason.${row.reason}`),
    },
    {
      id: 'assignedTo',
      header: t('followUps.columns.assignedTo'),
      cell: (row) => (
        <MemberNameLink
          memberId={row.assignedToMemberId}
          name={row.assignedToMemberName ?? ''}
        />
      ),
    },
    {
      id: 'status',
      header: t('followUps.columns.status'),
      cell: (row) => <Badge>{t(`followUps.status.${row.status}`)}</Badge>,
    },
  ];

  const rows = list.data?.data ?? [];

  return (
    <div>
      <PageHeader
        title={t('followUps.title')}
        description={t('followUps.description')}
        tipId="members.followUps.purpose"
      />
      <PeopleSubnav />
      <div className="mb-4 flex flex-wrap gap-2">
        {(['open', 'unassigned', 'overdue'] as const).map((item) => (
          <Button
            key={item}
            size="sm"
            variant={filter === item ? 'primary' : 'outline'}
            onClick={() => setFilter(item)}
          >
            {t(`followUps.filters.${item}`)}
          </Button>
        ))}
      </div>
      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        empty={<p className="text-body-sm text-fg-secondary">{t('followUps.empty')}</p>}
        onRowClick={(row) => setSelected(row)}
      />

      <FollowUpDialog
        followUp={selected}
        open={selected !== null}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
        onFollowUpUpdated={setSelected}
      />
    </div>
  );
};
